import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { login, logout } from "redux/auth/slice";
import { STORAGE_AUTH_TOKEN } from "constants/variables";
import {
  BonusCancelResponseType,
  BonusFreeSpinResponseDataType,
  BonusNominalResponseDataType,
  BonusResponseType,
  EditProfileRequest,
  EditProfileResponse,
  EmailRequestType,
  EmailResponseType,
  FreeSpinResponseType,
  SpinCancelResponseType,
  UserResponseType,
} from "types/User";
import { setLocalStorageItem } from "utils/localStorage";
import url from "url/index";
import { AvailableTriggerCompaingType } from "types/TriggerCompaings";
import { setServiceError } from "stores/settings";
import { setHostname } from "utils/hostname";
import { getAuthToken } from "helpers/authToken";
import {
  setBonuses,
  setFreeSpins,
  setUser,
  setTriggerCompaings,
  setBonusNominal,
} from "./slice";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: setHostname(),
    prepareHeaders: (headers) => {
      const authHeaders = getAuthToken();
      if (authHeaders) {
        Object.entries(authHeaders).forEach(([key, value]) => {
          headers.set(key, value as string);
        });
      }

      headers.set("f-v", import.meta.env.VITE_APP_VERSION_APP);

      return headers;
    },
  }),
  endpoints: (build) => ({
    getProfile: build.query<UserResponseType, string>({
      query: () => ({
        url: url.config.api.user.profile,
      }),
      keepUnusedDataFor: 10,
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.statusCode === 200) {
            dispatch(setUser(data.user));
            dispatch(login());
          } else {
            dispatch(logout());
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "Ошибка в queryFulfilled или вторичных запросах",
            error,
          );
        }
      },
    }),
    edit: build.mutation<EditProfileResponse, EditProfileRequest>({
      query: (request: EditProfileRequest) => ({
        url: url.config.api.user.edit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: EditProfileRequest,
        { dispatch, queryFulfilled },
      ) {
        const { data } = await queryFulfilled;
        if (data.statusCode === 200) {
          dispatch(setUser(data.user));
        }
      },
    }),
    email: build.mutation<EmailResponseType, EmailRequestType>({
      query: (request: EmailRequestType) => ({
        url: url.config.api.auth.changeEmail,
        method: "PATCH",
        body: request,
      }),
      async onQueryStarted(
        request: EmailRequestType,
        { dispatch, queryFulfilled, getState },
      ) {
        const { data } = await queryFulfilled;
        setLocalStorageItem(STORAGE_AUTH_TOKEN, data.authToken);
        const profile = await userApi.endpoints.getProfile.initiate("")(
          dispatch,
          getState,
          "",
        );
        if (profile.data) {
          dispatch(setUser(profile.data.user));
        }
      },
    }),
    bonuses: build.query<BonusResponseType, string>({
      query: (_) => ({
        url: url.config.api.user.bonus,
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data && data.data) {
          dispatch(setBonuses(data.data));
        }
        if (data.statusCode >= 500) {
          setServiceError(true);
        }
      },
    }),
    bonusNominal: build.query<
      Array<BonusNominalResponseDataType | BonusFreeSpinResponseDataType>,
      string
    >({
      query: (_) => ({
        url: url.config.api.rewardNominalUsers.userRewards,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;

        if (data) {
          dispatch(setBonusNominal(data));
        }
      },
    }),
    cancelBonusNominal: build.mutation<
      BonusCancelResponseType,
      number | string
    >({
      query: (id: number | string) => ({
        url: url.config.api.rewardNominalUsers.cancelBonusNominal(id),
        method: "POST",
      }),
      async onQueryStarted(
        request: number | string,
        { dispatch, queryFulfilled, getState },
      ) {
        const { data } = await queryFulfilled;

        await userApi.endpoints.bonusNominal.initiate("", {
          forceRefetch: true,
        })(dispatch, getState, "");
        if (data.statusCode >= 500) {
          setServiceError(true);
        }
      },
    }),
    availableTriggers: build.query<AvailableTriggerCompaingType[], string>({
      query: () => ({
        url: url.config.api.triggerCampains.availableTriggers,
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data) {
          dispatch(setTriggerCompaings(data));
        }
        // if (data.statusCode >= 500) {
        //   dispatch(setServiceError(true));
        // }
      },
    }),
    cancelBonus: build.mutation<BonusCancelResponseType, number | string>({
      query: (id: number | string) => ({
        url: url.config.api.bonuses.cancelBonus(id),
        method: "PATCH",
      }),
      async onQueryStarted(request: number | string, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.statusCode >= 500) {
          setServiceError(true);
        }
      },
    }),
    freeSpins: build.query<FreeSpinResponseType, string>({
      query: () => ({
        url: url.config.api.user.freeSpins,
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data && data.data) {
          dispatch(setFreeSpins(data.data));
        }
        if (data.statusCode >= 500) {
          setServiceError(true);
        }
      },
    }),
    cancelFreeSpin: build.mutation<SpinCancelResponseType, number | string>({
      query: (id: number | string) => ({
        url: url.config.api.bonuses.cancelFreeSpin(id),
        method: "PATCH",
      }),
      async onQueryStarted(request: number | string, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.statusCode >= 500) {
          setServiceError(true);
        }
      },
    }),
  }),
});
